import { useCallback, useEffect, useMemo, useState } from 'react'
import { PREP_KINDS, type Task, type TaskPrepGroup, type TaskPrepKind } from './types'
import type { PrepState } from './useTasks'
import { useI18n, type MsgKey } from './i18n'

/**
 * タスクの準備物（レイドに持ち込まないと詰むもの）の表示。
 * → doc/task-prep/master.md
 *
 * 一覧の行に畳んで出すぶん（PrepLine）と、登録タスク横断のまとめ（TaskPrepPanel）の両方。
 * 1つの候補リストは **「どれか1つでよい」** なので、2件以上には「いずれか」を付ける。
 */

const LS_CHECKED = 'tm.prepChecked'
const LS_PANEL = 'tm.prepPanel'

/** 畳んだときに見せる件数。62件並ぶタスク（Psycho Sniper）があるので必ず要る */
const FOLD_AT = 3

const KIND_INFO = new Map(PREP_KINDS.map((k) => [k.kind, k]))
const kindIcon = (kind: TaskPrepKind) => KIND_INFO.get(kind)?.icon ?? '•'
/** 種別の名前は言語で変わる。辞書のキーに落として引く（→ src/i18n.tsx） */
const kindKey = (kind: TaskPrepKind) => `prep.kind.${kind}` as MsgKey

/** 同じ中身のリストを1つにまとめるためのキー。チェックの保存キーも兼ねる */
export const groupKey = (group: TaskPrepGroup) =>
  `${group.kind}:${group.items.map((it) => it.id).join(',')}`

/** アイコンは id から組み立てられる。取れなければ黙って消す（名前だけで用は足りる） */
function ItemIcon({ id }: { id: string }) {
  const [ok, setOk] = useState(true)
  if (!ok) return null
  return (
    <img
      className="prep__icon"
      src={`https://assets.tarkov.dev/${id}-icon.webp`}
      alt=""
      loading="lazy"
      onError={() => setOk(false)}
    />
  )
}

/** 候補リスト1つ。先頭3件だけ出し、残りは「他N件」を押すと開く */
function PrepGroup({ group }: { group: TaskPrepGroup }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const rest = group.items.length - FOLD_AT
  const shown = open ? group.items : group.items.slice(0, FOLD_AT)

  return (
    <span className="prep__group">
      {shown.map((it, i) => (
        <span key={it.id} className="prep__item">
          {i > 0 && <span className="prep__sep">/</span>}
          <ItemIcon id={it.id} />
          {it.name}
          {it.count && it.count > 1 && <span className="prep__count">×{it.count}</span>}
        </span>
      ))}
      {rest > 0 && (
        <button type="button" className="prep__more" onClick={() => setOpen((v) => !v)}>
          {open ? t('prep.fold') : t('prep.more', { n: rest })}
        </button>
      )}
      {group.items.length > 1 && <span className="prep__any">{t('prep.any')}</span>}
    </span>
  )
}

/** 種別ごとにまとめた候補リスト。行の中でも、まとめパネルの中でも同じ形で使う */
function PrepGroups({ groups }: { groups: TaskPrepGroup[] }) {
  const { t } = useI18n()
  const byKind = useMemo(() => {
    const out = new Map<TaskPrepKind, TaskPrepGroup[]>()
    for (const g of groups) out.set(g.kind, [...(out.get(g.kind) ?? []), g])
    return [...out.entries()].sort(
      (a, b) => PREP_KINDS.findIndex((k) => k.kind === a[0]) - PREP_KINDS.findIndex((k) => k.kind === b[0]),
    )
  }, [groups])

  return (
    <ul className="prep__kinds">
      {byKind.map(([kind, list]) => (
        <li key={kind}>
          <span className="prep__kind" title={t(kindKey(kind))}>
            {kindIcon(kind)} {t(kindKey(kind))}
          </span>
          {list.map((g) => (
            <PrepGroup key={groupKey(g)} group={g} />
          ))}
        </li>
      ))}
    </ul>
  )
}

/**
 * 種別ごとの件数マーク（`🔑2 📌1`）。品名は出さない。
 * 開閉つきの `PrepLine` でも、畳んだ行の右端でも同じ形で使う。
 */
export function PrepChips({ prep }: { prep: TaskPrepGroup[] }) {
  const { t } = useI18n()
  if (!prep.length) return null

  const counts = new Map<TaskPrepKind, number>()
  for (const g of prep) counts.set(g.kind, (counts.get(g.kind) ?? 0) + 1)

  return (
    <>
      {PREP_KINDS.filter((k) => counts.has(k.kind)).map((k) => (
        <span key={k.kind} className="prep__chip" title={t(kindKey(k.kind))}>
          {k.icon}
          {counts.get(k.kind)}
        </span>
      ))}
    </>
  )
}

/**
 * 一覧の行に足す1行。畳んだ状態では種別と件数だけを出し、`▸` で品名を開く。
 * 開閉は行ごとのローカル状態（共有しない）。
 */
export function PrepLine({ prep }: { prep: TaskPrepGroup[] }) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  if (!prep.length) return null

  return (
    <div className={`prep prep--row${open ? ' prep--open' : ''}`}>
      <button
        type="button"
        className="prep__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={t('prep.line.title')}
      >
        <PrepChips prep={prep} />
        <span className="prep__caret">{open ? '▾' : '▸'}</span>
      </button>
      {open && <PrepGroups groups={prep} />}
    </div>
  )
}

type PanelProps = {
  /** 一覧に出ている順のタスクID（＝番号の元） */
  order: string[]
  byId: Map<string, Task>
  /** `👁` で非表示にしたタスク。準備物からも外す */
  hiddenIds: Set<string>
  /** アイテム名の取得ぐあい。16.7MB あるのでパネルを開くまで取りに行かない */
  prepState: PrepState
  /** パネルが開いたら呼ぶ。アイテム名の取得を促す */
  onNeedPrep: () => void
}

/** まとめパネルの1行。同じ物を要るタスクの番号を、名前つきで並べる */
type Merged = {
  key: string
  kind: TaskPrepKind
  group: TaskPrepGroup
  /** 一覧・マップと同じ番号。名前はマウスオーバーで出すために持つ */
  nos: { no: number; name: string }[]
}

/**
 * 🎒 準備物パネル。表示中のマップの登録タスク全部の持ち込み物を種別ごとにまとめる。
 * チェックはクライアントごとのローカル（各自の持ち物なので共有しない）。
 */
export function TaskPrepPanel({ order, byId, hiddenIds, prepState, onNeedPrep }: PanelProps) {
  const { lang, t } = useI18n()
  const [open, setOpen] = useState(() => localStorage.getItem(LS_PANEL) !== 'off')
  const [checked, setChecked] = useState<Set<string>>(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(LS_CHECKED) ?? '[]')
      return new Set(Array.isArray(raw) ? raw.filter((v): v is string => typeof v === 'string') : [])
    } catch {
      return new Set<string>()
    }
  })

  const save = useCallback((next: Set<string>) => {
    setChecked(next)
    localStorage.setItem(LS_CHECKED, JSON.stringify([...next]))
  }, [])

  const toggle = useCallback(
    (key: string) => {
      const next = new Set(checked)
      if (!next.delete(key)) next.add(key)
      save(next)
    },
    [checked, save],
  )

  // 開いた時点でアイテム名を取りに行く（→ doc/task-prep/master.md#16mbをどう扱うか）
  useEffect(() => {
    if (open) onNeedPrep()
  }, [open, onNeedPrep])

  const toggleOpen = useCallback(() => {
    setOpen((v) => {
      localStorage.setItem(LS_PANEL, v ? 'off' : 'on')
      return !v
    })
  }, [])

  // 同じ中身のリストは1行にまとめ、要るタスクの番号を並べる（例: 同じ鍵を②と③が要る）
  const merged = useMemo(() => {
    const out = new Map<string, Merged>()
    order.forEach((taskId, i) => {
      if (hiddenIds.has(taskId)) return
      const task = byId.get(taskId)
      if (!task) return
      for (const group of task.prep) {
        const key = groupKey(group)
        const hit = out.get(key)
        const no = { no: i + 1, name: task.name }
        if (hit) hit.nos.push(no)
        else out.set(key, { key, kind: group.kind, group, nos: [no] })
      }
    })
    return [...out.values()].sort(
      (a, b) =>
        PREP_KINDS.findIndex((k) => k.kind === a.kind) - PREP_KINDS.findIndex((k) => k.kind === b.kind) ||
        a.group.items[0].name.localeCompare(b.group.items[0].name, lang),
    )
  }, [order, byId, hiddenIds, lang])

  const taskCount = useMemo(
    () => order.filter((id) => !hiddenIds.has(id) && (byId.get(id)?.prep.length ?? 0) > 0).length,
    [order, byId, hiddenIds],
  )

  return (
    <section className={`prep prep--panel${open ? ' prep--open' : ''}`}>
      {/* 開閉は 🎒 の左。一覧の行の開閉ボタンと位置を揃える */}
      <button type="button" className="prep__head" onClick={toggleOpen} aria-expanded={open}>
        <span className="prep__caret">{open ? '▾' : '▸'}</span>
        <span>{t('prep.panel.title')}</span>
        <span className="prep__head-count">{t('prep.panel.count', { n: taskCount })}</span>
      </button>

      {open &&
        (prepState === 'loading' && merged.length === 0 ? (
          <p className="prep__empty">{t('prep.loading')}</p>
        ) : prepState === 'error' ? (
          <p className="prep__empty">
            {t('prep.error')}
            <button type="button" className="prep__clear" onClick={onNeedPrep}>
              {t('prep.retry')}
            </button>
          </p>
        ) : merged.length === 0 ? (
          <p className="prep__empty">{t('prep.none')}</p>
        ) : (
          <>
            {/* 右端の数字が何なのか（一覧・マップと同じタスク番号）を列名で示す */}
            <div className="prep__cols">
              <span>{t('prep.col.items')}</span>
              <span className="prep__nos" title={t('prep.col.no.title')}>
                {t('prep.col.no')}
              </span>
            </div>
            <ul className="prep__list">
              {PREP_KINDS.filter((k) => merged.some((m) => m.kind === k.kind)).map((k) => (
                <li key={k.kind}>
                  <span className="prep__kind">
                    {k.icon} {t(kindKey(k.kind))}
                  </span>
                  <ul>
                    {merged
                      .filter((m) => m.kind === k.kind)
                      .map((m) => (
                        <li key={m.key} className={checked.has(m.key) ? 'prep__done' : undefined}>
                          <label>
                            <input
                              type="checkbox"
                              checked={checked.has(m.key)}
                              onChange={() => toggle(m.key)}
                            />
                            <PrepGroup group={m.group} />
                          </label>
                          <span className="prep__nos">
                            {m.nos.map((n) => (
                              <span
                                key={n.no}
                                className="prep__no"
                                title={`${n.no}. ${n.name}`}
                              >
                                {n.no}
                              </span>
                            ))}
                          </span>
                        </li>
                      ))}
                  </ul>
                </li>
              ))}
            </ul>
            {checked.size > 0 && (
              <button type="button" className="prep__clear" onClick={() => save(new Set())}>
                {t('prep.clear')}
              </button>
            )}
          </>
        ))}
    </section>
  )
}
